import { apiClient } from '@/api/client'
import { publicClient } from '@/api/publicClient'

// ── Public (no-auth) types ───────────────────────────────────────────────────

/**
 * A service as the public page lists it — what a visitor picks between.
 *
 * No `price`, and so no per-option `currency`: showing no prices on the booking page
 * is the studio's decision (the catalog's prices are its own offer-writing helpers),
 * and the payload used to carry the number the page withholds.
 */
export interface PublicServiceOption {
  id: number
  name: string
  description: string | null
}

export interface PublicBookableMember {
  id: number
  name: string
}

export interface PublicBookingConfig {
  studioName: string
  studioDisplayName: string | null
  /** The studio's own privacy notice (https), or null when the platform notice is the one to link. */
  privacyNoticeUrl: string | null
  /** Composed studio address for the "At the studio" quick-fill (falls back to the name). */
  studioAddress: string | null
  logoUrl: string | null
  /** BCP-47 studio locale the public form renders in (no client exists yet for a lead). */
  locale: string | null
  /**
   * ISO-2 country hint for phone entry — the studio's own recorded country.
   * Null when unrecorded, in which case the widget falls back to the locale's
   * region subtag.
   */
  phoneCountry: string | null
  timezone: string
  currency: string
  /** The step (minutes) a client's drawn start time snaps to. */
  startSnapMinutes: number
  /** How many days ahead the studio takes bookings — the studio's real configured horizon. */
  horizonDays: number
  /**
   * The studio's inquiry-form slug for the "something else in mind?" escape hatch.
   * Null when the studio has no inquiry form enabled — the link is hidden then.
   */
  services: PublicServiceOption[]
  /** When true, the client may optionally pick a member from `members`. */
  allowMemberSelection: boolean
  members: PublicBookableMember[]
}

/** A free window (continuous open interval) — UTC instants as ISO strings. */
export interface AvailabilitySlot {
  start: string
  end: string
}

export interface PublicBookingRequestBody {
  serviceId: number
  requestedStart: string
  /** The drawn session length in minutes — required (services carry no duration). */
  durationMinutes: number
  /** Chosen team member; omit for "any available". */
  memberId?: number
  clientName: string
  clientEmail: string
  clientPhone?: string
  location?: string
  notes?: string
  leadSource?: string
  /**
   * OPTIONAL preferred correspondence language, a BCP-47 tag from `SUPPORTED_LOCALES`.
   * Omitted when the visitor left the picker untouched — the backend then leaves
   * `clients.locale` NULL, which already means "inherit the studio's language".
   */
  clientLocale?: string
  gdprConsent: boolean
  hpCompany: string
}

/**
 * The "something else in mind?" payload — the no-slot path of the same booking page.
 * No service, no start, no duration: the visitor has committed to neither, so the
 * lead is created untyped and without a hold. `requestedShootDate` is a wish
 * (`YYYY-MM-DD`), not a reservation.
 */
export interface PublicBookingLeadBody {
  clientName: string
  clientEmail: string
  clientPhone?: string
  requestedShootDate?: string
  location?: string
  notes?: string
  leadSource?: string
  /**
   * OPTIONAL preferred correspondence language, a BCP-47 tag from `SUPPORTED_LOCALES`.
   * Omitted when the visitor left the picker untouched — the backend then leaves
   * `clients.locale` NULL, which already means "inherit the studio's language".
   */
  clientLocale?: string
  gdprConsent: boolean
  hpCompany: string
}

// ── Authed (admin) types ─────────────────────────────────────────────────────

/*
 * `BookableService` / `BookableServiceInput` are GONE (IN-102). Booking had its own
 * table of priced services beside the price list, with the same shape and no link
 * between them. There is one catalog now — `PricingItem` in
 * `features/settings/pricingItems.ts` — and "bookable online" is a flag on a row of
 * it. The admin CRUD lives at `/pricing-items`; nothing under `/booking` edits
 * services any more.
 */

export type Weekday =
  | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY'
  | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'

export interface WorkingHoursEntry {
  dayOfWeek: Weekday
  startTime: string | null
  endTime: string | null
  enabled: boolean
  /** When true the studio takes bookings the whole calendar day; times are ignored. */
  allDay: boolean
}

export interface BlackoutDate {
  id: number
  startDate: string
  endDate: string
  /** "HH:mm:ss" when the blackout blocks only part of a single day; null = full-day. */
  startTime: string | null
  endTime: string | null
  reason: string | null
}

export interface BookingSettings {
  enabled: boolean
  slug: string | null
  availabilityMode: 'PER_RESOURCE' | 'STUDIO_WIDE'
  ignoreCalendarConflicts: boolean
  startSnapMinutes: number
  bufferMinutes: number
  minLeadHours: number
  horizonDays: number
  holdHours: number
  allowMemberSelection: boolean
  /**
   * READ-ONLY. How many services the PUBLIC page would offer a client right now —
   * the size of `BookableCatalog.listBookableForCurrentOrg()`, the same call the page
   * itself renders from, so the two cannot disagree. Not a catalogue statistic: an
   * item that is bookable-online and active still does not count while its service
   * type is archived, because the page drops it.
   *
   * Served here rather than by a `/pricing-items?bookableOnline=true&active=true`
   * count precisely because that shape has to restate the rule and drifted from it
   * twice. Omitted from `BookingSettingsUpdate` — there is nothing to PATCH.
   */
  bookableServiceCount: number
  /**
   * READ-ONLY (RB-39 C, D81). The org's active service types split by that same list:
   * offered = at least one row the public page renders belongs to it. Names, catalog
   * order. Optional so an older body renders no line rather than a wrong one.
   */
  offeredServiceTypes?: string[]
  notOfferedServiceTypes?: string[]
}

export type BookingSettingsUpdate = Partial<Omit<BookingSettings,
  'slug' | 'bookableServiceCount' | 'offeredServiceTypes' | 'notOfferedServiceTypes'>>

export interface BookableMember {
  id: number
  name: string
  email: string
  bookable: boolean
}

export type BookingRequestStatus =
  | 'PENDING' | 'APPROVED' | 'EXPIRED' | 'CANCELLED'

export interface BookingRequest {
  id: number
  bookableServiceId: number
  serviceName: string
  durationMinutes: number
  requestedStart: string
  requestedEnd: string
  assignedMemberId: number | null
  assignedMemberName: string | null
  clientName: string
  clientEmail: string
  clientPhone: string | null
  location: string | null
  notes: string | null
  leadSource: string | null
  status: BookingRequestStatus
  holdExpiresAt: string | null
  createdProjectId: number | null
  createdShootId: number | null
  createdAt: string
}

/** Unconfirmed form/ingest inquiry — from GET /booking/requests/form-inquiries. */
export interface FormInquiry {
  projectId: number
  clientName: string
  clientEmail: string
  /** Nullable — the public form asks for a phone but does not require one. */
  clientPhone: string | null
  /** Null for an untyped lead — what the card's one-click type picker sets. */
  serviceTypeId: number | null
  /** Nullable — an untyped lead picked no service, so there was no type to derive. */
  serviceTypeName: string | null
  /** The message the client typed on the form; nullable. */
  notes: string | null
  leadSource: string | null
  /**
   * `YYYY-MM-DD`. The no-slot lead's only date — a wish, not a reservation.
   * Nullable: the field is optional on the form, and ingest webhooks rarely carry one.
   */
  requestedShootDate: string | null
  /** Where the client would like to shoot; nullable. */
  location: string | null
  /** Where this project came from — server-set; drives the card's channel label (RB-03 F3). */
  origin: 'WEBSITE_FORM' | 'BOOKING_PAGE' | 'EMAIL' | 'MANUAL'
  createdAt: string
}

/**
 * A settled form/ingest inquiry — from GET /booking/requests/inquiry-history.
 * Either confirmed and moved past INQUIRY (`dismissed: false`, `status` is the
 * project's current status) or dismissed before it became work (`dismissed: true`,
 * the project is soft-deleted so there is nothing to link to).
 */
export interface InquiryHistoryItem {
  projectId: number
  title: string | null
  clientName: string
  serviceTypeName: string | null
  status: string
  dismissed: boolean
  confirmedAt: string | null
  /** Where this project came from — server-set; drives the row's channel label (RB-03 F3). */
  origin: 'WEBSITE_FORM' | 'BOOKING_PAGE' | 'EMAIL' | 'MANUAL'
  createdAt: string
}

/**
 * Payload for `bookingAdminApi.confirmInquiry` / `previewInquiryConfirmation` (RB-03 F4).
 * Wall-clock `YYYY-MM-DDTHH:mm:00` in the studio's calendar timezone, resolved
 * server-side. Both null = "confirm without a date"; exactly one null is rejected (400).
 */
export interface ConfirmInquiryPayload {
  startLocal: string | null
  endLocal: string | null
}

/** From `POST /booking/requests/form-inquiries/{projectId}/confirm-preview` — sends nothing. */
export interface InquiryConfirmationPreview {
  to: string
  subject: string
  html: string
}

/** Just enough of `ProjectResponse` for the confirm dialog's caller to act on the result. */
export interface ProjectLike {
  id: number
  status: string
  inquiryConfirmedAt: string | null
}

export interface AssignableMember {
  id: number
  name: string
  free: boolean
}

// ── Public API ────────────────────────────────────────────────────────────────

export const publicBookingApi = {
  config: (slug: string) =>
    publicClient.get<PublicBookingConfig>(`/public/booking/${slug}/config`).then(r => r.data),
  /** Free windows (continuous {start,end} intervals) — duration- and service-independent. */
  availability: (slug: string, from: string, to: string, memberId?: number) =>
    publicClient
      .get<AvailabilitySlot[]>(`/public/booking/${slug}/availability`, {
        params: {
          from, to,
          ...(memberId ? { memberId } : {}),
        },
      })
      .then(r => r.data),
  submit: (slug: string, body: PublicBookingRequestBody) =>
    publicClient.post<{ message: string; reference: string | null }>(`/public/booking/${slug}`, body).then(r => r.data),
  /** No-slot lead ("something else in mind?") — same page, same slug, no hold. */
  submitLead: (slug: string, body: PublicBookingLeadBody) =>
    publicClient.post<{ message: string; reference: string | null }>(`/public/booking/${slug}/lead`, body).then(r => r.data),
}

// ── Authed API ──────────────────────────────────────────────────────────────

export const bookingAdminApi = {
  // working hours
  getWorkingHours: () =>
    apiClient.get<WorkingHoursEntry[]>('/booking/working-hours').then(r => r.data),
  updateWorkingHours: (days: WorkingHoursEntry[]) =>
    apiClient.put<WorkingHoursEntry[]>('/booking/working-hours', { days }).then(r => r.data),

  // blackout dates
  listBlackoutDates: () =>
    apiClient.get<BlackoutDate[]>('/booking/blackout-dates').then(r => r.data),
  createBlackoutDate: (body: {
    startDate: string; endDate?: string | null
    startTime?: string | null; endTime?: string | null // "HH:mm" for a partial single-day range
    reason?: string | null
  }) =>
    apiClient.post<BlackoutDate>('/booking/blackout-dates', body).then(r => r.data),
  deleteBlackoutDate: (id: number) => apiClient.delete(`/booking/blackout-dates/${id}`),

  // settings
  getSettings: () => apiClient.get<BookingSettings>('/booking/settings').then(r => r.data),
  updateSettings: (patch: BookingSettingsUpdate) =>
    apiClient.patch<BookingSettings>('/booking/settings', patch).then(r => r.data),
  regenerateSlug: () =>
    apiClient.post<BookingSettings>('/booking/settings/regenerate-slug').then(r => r.data),

  // members
  listMembers: () => apiClient.get<BookableMember[]>('/booking/members').then(r => r.data),
  updateMemberBookable: (id: number, bookable: boolean) =>
    apiClient.patch<BookableMember>(`/booking/members/${id}`, { bookable }).then(r => r.data),

  // requests inbox
  listRequests: (status?: BookingRequestStatus) =>
    apiClient
      .get<BookingRequest[]>('/booking/requests', { params: status ? { status } : {} })
      .then(r => r.data),
  pendingCount: () =>
    apiClient.get<{ count: number }>('/booking/requests/count').then(r => r.data.count),
  // Unconfirmed form/ingest inquiries (INQUIRY projects not linked to a pending booking request).
  listFormInquiries: () =>
    apiClient.get<FormInquiry[]>('/booking/requests/form-inquiries').then(r => r.data),
  // Settled form/ingest inquiries (confirmed-and-moved-on + dismissed) — the half of
  // the Inquiries History section that booking requests do not cover.
  listInquiryHistory: () =>
    apiClient.get<InquiryHistoryItem[]>('/booking/requests/inquiry-history').then(r => r.data),
  getRequest: (id: number) =>
    apiClient.get<BookingRequest>(`/booking/requests/${id}`).then(r => r.data),
  /**
   * The booking request behind a project, or null when the lead arrived without a slot.
   * 204 (not 404) is the "no booking" answer, so `r.data` is the empty string — coerce it
   * to null rather than letting an empty string reach a component that expects an object.
   */
  getRequestForProject: (projectId: number) =>
    apiClient
      .get<BookingRequest | ''>(`/booking/requests/by-project/${projectId}`)
      .then(r => (r.status === 204 || !r.data ? null : (r.data as BookingRequest))),
  assignees: (id: number) =>
    apiClient.get<AssignableMember[]>(`/booking/requests/${id}/assignees`).then(r => r.data),
  approve: (id: number, assignedMemberId: number | null) =>
    apiClient.post<BookingRequest>(`/booking/requests/${id}/approve`, { assignedMemberId }).then(r => r.data),
  // Dismiss a lead: soft-deletes the linked INQUIRY project (cascade) + releases the hold.
  deleteInquiry: (id: number) =>
    apiClient.post<BookingRequest>(`/booking/requests/${id}/delete-inquiry`).then(r => r.data),
  // Confirm a form/ingest inquiry (RB-03 F4, decision D1): always emails the client.
  // Both fields null = "confirm without a date". `startLocal`/`endLocal` are wall-clock
  // `YYYY-MM-DDTHH:mm:00` strings, interpreted in the studio's calendar timezone
  // server-side — never a browser-zone instant.
  confirmInquiry: (projectId: number, payload: ConfirmInquiryPayload) =>
    apiClient
      .post<ProjectLike>(`/booking/requests/form-inquiries/${projectId}/confirm`, payload)
      .then(r => r.data),
  // The exact email confirmInquiry would send for this project and window — sends
  // nothing, writes nothing. Same payload shape as confirmInquiry.
  previewInquiryConfirmation: (projectId: number, payload: ConfirmInquiryPayload) =>
    apiClient
      .post<InquiryConfirmationPreview>(`/booking/requests/form-inquiries/${projectId}/confirm-preview`, payload)
      .then(r => r.data),
}
